/**
 * Geo Module
 *
 * Provides geocoding and routing capabilities through vendor-agnostic interfaces.
 *
 * This module is self-contained within sdk-lanes but can be extracted to
 * a separate sdk-geo package if needed in the future.
 *
 * ## Quick Start
 *
 * ```typescript
 * import { createGoogleProviders } from '@hopdrive/sdk-lanes/geo'
 *
 * const { geocoding, routing } = createGoogleProviders(apiKey) // From your app's configuration
 *
 * // Geocode an address
 * const location = await geocoding.geocode({ address: '123 Main St, Austin, TX' })
 * console.log(location.latitude, location.longitude)
 *
 * // Compute a route
 * const route = await routing.computeRoute({
 *   origin: { latitude: 30.2672, longitude: -97.7431 },
 *   destination: { latitude: 29.4241, longitude: -98.4936 },
 *   includePolyline: true,
 *   includeTolls: true,
 * })
 * console.log(route.distanceMeters, route.tolls)
 * ```
 *
 * ## Adding a New Provider
 *
 * 1. Create a new folder under `providers/` (e.g., `providers/here/`)
 * 2. Implement `GeocodingProvider` and/or `RoutingProvider` interfaces
 * 3. Export from `providers/index.ts`
 * 4. Add a factory function if needed
 *
 * ## Architecture
 *
 * The module uses a layered architecture:
 *
 * ```
 * Application Code (sdk-lanes features)
 *         ↓
 * Provider Interfaces (GeocodingProvider, RoutingProvider)
 *         ↓
 * Provider Implementations (GoogleGeocodingProvider, etc.)
 *         ↓
 * External APIs (Google Maps Platform)
 * ```
 */

// Types
export * from './types'

// Providers
export * from './providers'

// Contract validation utilities (useful for testing provider implementations)
export * from './contracts'

// Factory functions
import {
  GoogleClient,
  GoogleGeocodingProvider,
  GoogleRoutesProvider,
  GoogleSuggestionProvider,
  type GoogleClientConfig,
} from './providers/google'
import type { GeocodingProvider } from './types/geocoding'
import type { RoutingProvider } from './types/routing'
import type { SuggestionProvider } from './types/suggestion'

/**
 * Configuration for Google providers
 *
 * Requires a Google API key with Geocoding API, Routes API, and
 * Places API (New) enabled.
 */
export interface GoogleProvidersConfig {
  /** Google API key with Geocoding, Routes, and Places API (New) enabled */
  apiKey: string
  /** Optional client configuration overrides */
  clientConfig?: Omit<GoogleClientConfig, 'apiKey'>
}

/**
 * Create Google geocoding, routing, and suggestion providers
 *
 * This is the recommended way to instantiate Google providers.
 * The client is shared between providers for efficient connection reuse.
 *
 * @example
 * ```typescript
 * const { geocoding, routing, suggestion } = createGoogleProviders({
 *   apiKey: apiKey // From your app's configuration
 * })
 * ```
 */
export function createGoogleProviders(config: GoogleProvidersConfig | string): {
  geocoding: GeocodingProvider
  routing: RoutingProvider
  suggestion: SuggestionProvider
  client: GoogleClient
} {
  const apiKey = typeof config === 'string' ? config : config.apiKey
  const clientConfig = typeof config === 'string' ? {} : config.clientConfig

  const client = new GoogleClient({
    apiKey,
    ...clientConfig,
  })

  return {
    geocoding: new GoogleGeocodingProvider(client),
    routing: new GoogleRoutesProvider(client),
    suggestion: new GoogleSuggestionProvider(client),
    client,
  }
}

/**
 * Create a Google address suggestion provider
 *
 * Use this when you only need address autocomplete capabilities.
 *
 * @example
 * ```typescript
 * const suggester = createGoogleSuggester(apiKey) // From your app's configuration
 * const result = await suggester.suggest({ input: '123 Main' })
 * ```
 */
export function createGoogleSuggester(
  config: GoogleProvidersConfig | string
): SuggestionProvider {
  const apiKey = typeof config === 'string' ? config : config.apiKey
  const clientConfig = typeof config === 'string' ? {} : config.clientConfig

  const client = new GoogleClient({
    apiKey,
    ...clientConfig,
  })

  return new GoogleSuggestionProvider(client)
}

/**
 * Create a Google geocoding provider
 *
 * Use this when you only need geocoding capabilities.
 *
 * @example
 * ```typescript
 * const geocoder = createGoogleGeocoder(apiKey) // From your app's configuration
 * const result = await geocoder.geocode({ address: '1600 Amphitheatre Parkway' })
 * ```
 */
export function createGoogleGeocoder(
  config: GoogleProvidersConfig | string
): GeocodingProvider {
  const apiKey = typeof config === 'string' ? config : config.apiKey
  const clientConfig = typeof config === 'string' ? {} : config.clientConfig

  const client = new GoogleClient({
    apiKey,
    ...clientConfig,
  })

  return new GoogleGeocodingProvider(client)
}

/**
 * Create a Google routing provider
 *
 * Use this when you only need routing capabilities.
 *
 * @example
 * ```typescript
 * const router = createGoogleRouter(apiKey) // From your app's configuration
 * const route = await router.computeRoute({
 *   origin: { latitude: 30.27, longitude: -97.74 },
 *   destination: { latitude: 29.42, longitude: -98.49 }
 * })
 * ```
 */
export function createGoogleRouter(
  config: GoogleProvidersConfig | string
): RoutingProvider {
  const apiKey = typeof config === 'string' ? config : config.apiKey
  const clientConfig = typeof config === 'string' ? {} : config.clientConfig

  const client = new GoogleClient({
    apiKey,
    ...clientConfig,
  })

  return new GoogleRoutesProvider(client)
}
